import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { portfolioData } from '../data/mockData';

const VideoCard = ({ item }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef(null);
  
  // Reset state when item changes
  useEffect(() => {
    setIsPlaying(false);
    setIsLoading(false);
  }, [item.id]);

  const handlePlayPause = async () => {
    if (!item.isRealVideo || !videoRef.current) return;
    
    try {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        
        // Play video
        const playPromise = videoRef.current.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          setIsPlaying(true);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error('Video playback error:', error);
      setIsLoading(false);
      
      // Try playing muted if autoplay was blocked
      if (error.name === 'NotAllowedError') {
        try {
          videoRef.current.muted = true;
          setIsMuted(true);
          await videoRef.current.play();
          setIsPlaying(true);
        } catch (e) {
          console.error('Muted playback also failed:', e);
        }
      }
    }
  };

  const handleMuteToggle = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
  };

  const handleWaiting = () => {
    if (isPlaying) {
      setIsLoading(true);
    }
  };

  const handlePlaying = () => {
    setIsLoading(false);
    setIsPlaying(true);
  };

  return (
    <div className="group cursor-pointer">
      <div 
        className="relative aspect-[9/16] overflow-hidden mb-4 border border-[#2a2a2a] bg-[#0a0a0a]"
        onClick={handlePlayPause}
      >
        {item.isRealVideo ? (
          <>
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              muted={isMuted}
              playsInline
              loop
              preload="auto"
              onEnded={handleVideoEnd}
              onCanPlay={handleCanPlay}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
            >
              <source src={item.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Loading Overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-[#0a0a0a]/60 flex items-center justify-center z-10">
                <Loader2 size={32} className="text-[#c9a962] animate-spin" />
              </div>
            )}
            
            {/* Play/Pause Overlay */}
            {!isLoading && (
              <div className={`absolute inset-0 bg-[#0a0a0a]/30 flex items-center justify-center transition-opacity duration-300 ${isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}>
                <div className="w-16 h-16 bg-[#c9a962] rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                  {isPlaying ? (
                    <Pause size={24} className="text-[#0a0a0a]" fill="#0a0a0a" />
                  ) : (
                    <Play size={24} className="text-[#0a0a0a] ml-1" fill="#0a0a0a" />
                  )}
                </div>
              </div>
            )}
            
            {/* Mute Button */}
            {isPlaying && !isLoading && (
              <button
                onClick={handleMuteToggle}
                className="absolute bottom-4 right-4 w-10 h-10 bg-[#0a0a0a]/70 rounded-full flex items-center justify-center hover:bg-[#0a0a0a] transition-colors z-20"
              >
                {isMuted ? (
                  <VolumeX size={18} className="text-white" />
                ) : (
                  <Volume2 size={18} className="text-white" />
                )}
              </button>
            )}
          </>
        ) : (
          <>
            <img 
              src={item.thumbnail}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Play Button Overlay for placeholder videos */}
            <div className="absolute inset-0 bg-[#0a0a0a]/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-16 h-16 bg-[#c9a962] rounded-full flex items-center justify-center">
                <Play size={24} className="text-[#0a0a0a] ml-1" fill="#0a0a0a" />
              </div>
            </div>
          </>
        )}
      </div>
      <h3 className="heading-3 text-white mb-1">
        {item.title}
      </h3>
      <p className="body-small text-[#808080]">
        {item.description}
      </p>
    </div>
  );
};

const PhotoCard = ({ item }) => {
  return (
    <div className="group cursor-pointer">
      <div className="relative aspect-square overflow-hidden mb-4 image-hover border border-[#2a2a2a]">
        <img 
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <h3 className="heading-3 text-white mb-1">
        {item.title}
      </h3>
      <p className="body-small text-[#808080]">
        {item.description}
      </p>
    </div>
  );
};

const Portfolio = () => {
  return (
    <section id="portfolio" className="section-padding bg-[#111111]">
      <div className="container">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block text-sm font-medium text-[#c9a962] tracking-wider uppercase mb-4">
            My Work
          </span>
          
          <h2 className="heading-1 text-white mb-6">
            Portfolio
          </h2>
          
          <p className="body-large text-[#b3b3b3]">
            A selection of content I've created for restaurants, cafés, hotels, and beauty brands.
          </p>
        </div>
        
        {/* Tabs */}
        <Tabs defaultValue="videos" className="w-full">
          <TabsList className="w-full max-w-md mx-auto mb-12 bg-transparent border border-[#2a2a2a] rounded-none h-auto p-1">
            <TabsTrigger 
              value="videos" 
              className="flex-1 rounded-none data-[state=active]:bg-[#c9a962] data-[state=active]:text-[#0a0a0a] text-[#b3b3b3] py-3 text-sm font-medium transition-all duration-300"
            >
              Videos
            </TabsTrigger>
            <TabsTrigger 
              value="photos" 
              className="flex-1 rounded-none data-[state=active]:bg-[#c9a962] data-[state=active]:text-[#0a0a0a] text-[#b3b3b3] py-3 text-sm font-medium transition-all duration-300"
            >
              Photos
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="videos" className="mt-0">
            {portfolioData.videos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {portfolioData.videos.map((item) => (
                  <VideoCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 border border-[#2a2a2a] bg-[#0a0a0a]/50">
                <p className="text-[#808080] body-large mb-2">Video content coming soon</p>
                <p className="text-[#666666] body-small">Check back later for new video projects</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="photos" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {portfolioData.photos.map((item) => (
                <PhotoCard key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Portfolio;
